// apps/web/src/features/crm/leads/list/EngagementListFilters.tsx
//
// Secondary filters for the v4 lead list (#2247 FR-1): a 1..5 score narrow and
// a bound-client narrow. Rendered inside the collapsible `FilterPanel`; the
// free-text search is a primary always-visible control in the page header, not
// here. The client narrow reuses the shared `LeadClientPicker`
// and surfaces the active client as a `leads:list.clientActive` chip with a clear
// affordance. Both narrow WITHIN the caller's visible scope.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormRow, IconButton, Select, type SelectOption } from '../../../../ui/index.js';
import { LeadClientPicker } from './LeadClientPicker.js';

export type ScoreFilter = '' | '1' | '2' | '3' | '4' | '5';

export interface EngagementListFiltersProps {
  score: ScoreFilter;
  onScore: (v: ScoreFilter) => void;
  client: { id: string; name: string } | null;
  onClientPick: (client: { id: string; name: string }) => void;
  onClientClear: () => void;
}

export function EngagementListFilters({
  score,
  onScore,
  client,
  onClientPick,
  onClientClear,
}: EngagementListFiltersProps) {
  const { t } = useTranslation(['leads', 'common']);
  const [pickerOpen, setPickerOpen] = useState(false);

  const scoreOptions: SelectOption[] = [
    { value: '', label: t('leads:list.scoreAll') },
    ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) })),
  ];

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <FormRow label={t('leads:list.scoreLabel')}>
        <Select value={score} onChange={(v) => onScore(v as ScoreFilter)} options={scoreOptions} />
      </FormRow>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--fg-2)' }}>{t('leads:list.clientLabel')}</span>
        {client ? (
          <span
            data-testid="engagement-client-filter-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 6px 0 12px',
              borderRadius: 'var(--r-full)',
              background: 'var(--accent-1-subtle)',
              color: 'var(--accent-1-press)',
              fontSize: 13,
            }}
          >
            {t('leads:list.clientActive', { name: client.name })}
            <IconButton
              icon="x"
              label={t('leads:list.clientClear')}
              size={24}
              onClick={onClientClear}
              data-testid="engagement-client-filter-clear"
            />
          </span>
        ) : (
          <Button
            kind="ghost"
            icon="users"
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="engagement-client-filter-toggle"
          >
            {pickerOpen ? t('common:actions.cancel') : t('leads:list.clientLabel')}
          </Button>
        )}
        {pickerOpen && !client && (
          <LeadClientPicker
            onPick={(c) => {
              onClientPick(c);
              setPickerOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
